import {
  createSyncMutation,
  isCursorExpired,
  MutationQueue,
  PersonalIndexedDB,
  SyncEngine,
  type OfflineMutation,
  type SyncMutation,
  type SyncReport,
  type SyncStore,
} from '@prompt-assistant/sync';
import type { Playbook } from '@prompt-assistant/contracts';
import { APIClientError, api } from './api';

export type QueuedPlaybookMutation = Pick<Playbook, 'kind' | 'title' | 'description' | 'body' | 'variables' | 'tags'> & {
  workspace_id?: string;
  version?: number;
};

type StoredValue =
  | { kind: 'account'; userId: string; personalWorkspaceId?: string }
  | { kind: 'playbook'; item: Playbook }
  | { kind: 'cursor'; workspaceId: string; cursor: number; snapshotToken?: string }
  | { kind: 'mutation'; record: OfflineMutation<QueuedPlaybookMutation> };

const persistence = new PersonalIndexedDB<StoredValue>({ databaseName: 'prompt-assistant', storeName: 'sync-v2', version: 2 });
let queue = new MutationQueue<QueuedPlaybookMutation>();
const localPlaybooks = new Map<string, Playbook>();
const cursors = new Map<string, { cursor: number; snapshotToken?: string }>();
let accountUserId = '';
let personalWorkspaceId = '';
let knownTeams = new Set<string>();
let hydrated = false;
let hydratePromise: Promise<void> | null = null;
let syncPromise: Promise<SyncReport> | null = null;
let syncAgain = false;
let eventSocket: WebSocket | null = null;
let eventReconnectTimer: number | null = null;

function idFor(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes);
  else bytes.forEach((_byte, index) => { bytes[index] = Math.floor(Math.random() * 256); });
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  return [...bytes].map((byte, index) => `${byte.toString(16).padStart(2, '0')}${[3, 5, 7, 9].includes(index) ? '-' : ''}`).join('');
}

async function safePut(key: string, value: StoredValue): Promise<void> {
  try { await persistence.put(key, value); } catch { /* private browsing or unavailable IndexedDB */ }
}

async function safeDelete(key: string): Promise<void> {
  try { await persistence.delete(key); } catch { /* memory remains the active fallback */ }
}

async function safeClear(): Promise<void> {
  try { await persistence.clear(); } catch { /* memory remains the active fallback */ }
}

function normalizePlaybook(value: Partial<Playbook>, fallbackId: string, fallbackWorkspaceId: string, fallbackVersion = 0): Playbook & { latest_release_id?: string } {
  const now = new Date().toISOString();
  return {
    id: value.id ?? fallbackId,
    workspace_id: value.workspace_id ?? fallbackWorkspaceId,
    kind: value.kind === 'content_skill' ? 'content_skill' : 'prompt',
    title: value.title ?? 'Untitled Prompt',
    description: value.description ?? '',
    body: value.body ?? '',
    variables: value.variables ?? [],
    tags: value.tags ?? [],
    created_at: value.created_at ?? now,
    updated_at: value.updated_at ?? now,
    version: value.version ?? fallbackVersion,
    etag: value.etag ?? '',
    source: value.source === 'team' ? 'team' : 'personal',
    archived_at: value.archived_at,
    latest_release_id: (value as Playbook & { latest_release_id?: string }).latest_release_id,
    template_origin: value.template_origin,
  };
}

async function hydrate(userId: string): Promise<void> {
  const storedEntries = await persistence.entries().catch(() => [] as Array<{key: string; value: StoredValue}>);
  const storedAccount = storedEntries.find(({ key }) => key === 'account')?.value;
  if (storedAccount?.kind === 'account' && storedAccount.userId !== userId) {
    await safeClear();
    storedEntries.length = 0;
  }
  queue = new MutationQueue<QueuedPlaybookMutation>();
  localPlaybooks.clear();
  cursors.clear();
  personalWorkspaceId = storedAccount?.kind === 'account' && storedAccount.userId === userId ? storedAccount.personalWorkspaceId ?? '' : '';
  if (!personalWorkspaceId) {
    const previousPersonal = storedEntries.find(({value}) => value.kind === 'playbook' && value.item.source === 'personal')?.value;
    if (previousPersonal?.kind === 'playbook') personalWorkspaceId = previousPersonal.item.workspace_id;
  }
  knownTeams = new Set(storedEntries.flatMap(({ value }) => value.kind === 'playbook' && value.item.source === 'team' ? [value.item.workspace_id] : []));
  for (const { key, value } of storedEntries) {
    const workspaceId = value.kind === 'playbook' ? value.item.workspace_id : value.kind === 'cursor' ? value.workspaceId : value.kind === 'mutation' ? value.record.workspaceId : '';
    if (knownTeams.has(workspaceId)) { await safeDelete(key); continue; }
    if (value.kind === 'playbook') localPlaybooks.set(value.item.id, value.item);
    if (value.kind === 'cursor') cursors.set(value.workspaceId, { cursor: value.cursor, snapshotToken: value.snapshotToken });
    if (value.kind === 'mutation') queue.restore(value.record);
  }
  accountUserId = userId;
  hydrated = true;
  await safePut('account', { kind: 'account', userId, personalWorkspaceId });
}

export async function initializeSyncAccount(userId: string): Promise<void> {
  if (hydrated && accountUserId === userId) return;
  if (hydratePromise) await hydratePromise;
  if (hydrated && accountUserId === userId) return;
  // Do not swap the in-memory store while a previous account is still
  // applying a remote response. The next account must start from a quiescent
  // queue/cursor set, otherwise an old response could repopulate new-user UI.
  if (syncPromise) {
    try { await syncPromise; } catch { /* the old account can still be rehydrated safely */ }
  }
  if (hydrated && accountUserId === userId) return;
  hydratePromise = hydrate(userId).finally(() => { hydratePromise = null; });
  await hydratePromise;
}

async function purgeWorkspace(workspaceId: string): Promise<void> {
  for (const [id, item] of localPlaybooks) if (item.workspace_id === workspaceId) {
    localPlaybooks.delete(id);
    await safeDelete(`playbook:${id}`);
  }
  cursors.delete(workspaceId);
  await safeDelete(`cursor:${workspaceId}`);
  for (const record of queue.snapshot()) if (record.workspaceId === workspaceId) {
    queue.remove(record.id);
    await safeDelete(`outbox:${record.id}`);
  }
}

async function applyStatus(workspaces: Array<{ workspace_id: string; kind?: string }>): Promise<void> {
  const personal = workspaces.find((workspace) => workspace.kind === 'personal');
  const currentTeams = new Set(workspaces.filter((w) => w.kind === 'team').map((w) => w.workspace_id));
  for (const workspaceId of knownTeams) if (!currentTeams.has(workspaceId)) emitSyncNotification({type: 'workspace_revoked', workspace_id: workspaceId});
  knownTeams = currentTeams;
  if (!personal) throw new Error('Personal workspace unavailable');
  personalWorkspaceId = personal.workspace_id;
  await safePut('account', {kind: 'account', userId: accountUserId, personalWorkspaceId});
  // Purge legacy Team rows, cursors and queued writes, including memberships
  // that disappeared while disconnected. Only Personal may enter this store.
  const storedWorkspaces = new Set([...localPlaybooks.values()].map((p) => p.workspace_id).concat([...cursors.keys()], queue.snapshot().map((r) => r.workspaceId)));
  for (const id of storedWorkspaces) if (id !== personalWorkspaceId && id !== 'personal') await purgeWorkspace(id);
  queue.rebindWorkspace('personal', personalWorkspaceId);
  for (const record of queue.snapshot()) await safePut(`outbox:${record.id}`, { kind: 'mutation', record });
}

const store: SyncStore<Partial<Playbook>> = {
  async getCursor(workspaceId) { return cursors.get(workspaceId)?.cursor; },
  async putCursor(workspaceId, cursor, snapshotToken) {
    const next = { cursor, snapshotToken: snapshotToken ?? cursors.get(workspaceId)?.snapshotToken };
    cursors.set(workspaceId, next);
    await safePut(`cursor:${workspaceId}`, { kind: 'cursor', workspaceId, ...next });
  },
  async replaceWorkspace(workspaceId, items, cursor, snapshotToken) {
    const pendingIds = new Set(queue.snapshot().filter((record) => record.workspaceId === workspaceId && ['queued', 'failed', 'in_flight'].includes(record.status)).map((record) => record.entityId));
    for (const [id, item] of localPlaybooks) {
      if (item.workspace_id === workspaceId && !pendingIds.has(id)) {
        localPlaybooks.delete(id);
        await safeDelete(`playbook:${id}`);
      }
    }
    for (const item of items) if (!pendingIds.has(item.id)) await this.upsert('playbook', item);
    await this.putCursor(workspaceId, cursor, snapshotToken);
  },
  async upsert(entity, item) {
    if (entity !== 'playbook' || !item.id) return;
    const normalized = normalizePlaybook(item, item.id, item.workspace_id ?? personalWorkspaceId);
    if (normalized.source === 'team' || (normalized.workspace_id !== personalWorkspaceId && normalized.workspace_id !== 'personal')) return;
    localPlaybooks.set(normalized.id, normalized);
    await safePut(`playbook:${normalized.id}`, { kind: 'playbook', item: normalized });
  },
  async remove(entity, entityId) {
    if (entity !== 'playbook') return;
    localPlaybooks.delete(entityId);
    await safeDelete(`playbook:${entityId}`);
  },
  async listOutbox() { return queue.snapshot(); },
  async removeOutbox(id) {
    queue.remove(id);
    await safeDelete(`outbox:${id}`);
  },
  async markOutbox(id, status, error) {
    if (status === 'failed') queue.markFailed(id, error ?? 'SYNC_FAILED');
    else if (status === 'conflict') queue.markConflict(id, error ?? 'VERSION_CONFLICT');
    else queue.setStatus(id, status, error);
    const record = queue.snapshot().find((candidate) => candidate.id === id);
    if (record) await safePut(`outbox:${id}`, { kind: 'mutation', record });
  },
  async preserveConflict(operation, current) {
    const local = normalizePlaybook({ ...current, ...operation.payload }, idFor(), operation.workspaceId, operation.baseVersion ?? 0);
    local.title = `${local.title}（冲突副本）`;
    local.version = 0;
    local.etag = '';
    local.source = 'personal';
    await this.upsert('playbook', local);
  },
};

const remote = {
  async status() {
    const result = await api.syncStatus();
    await applyStatus(result.workspaces);
    return {...result, workspaces: result.workspaces.filter((w) => w.kind === 'personal')};
  },
  async bootstrap(workspaceId: string) { return api.syncBootstrap(workspaceId); },
  async delta(workspaceId: string, cursor: number, limit = 100) { return api.syncDelta(workspaceId, cursor, limit); },
  async fetch(entity: string, entityId: string): Promise<Partial<Playbook>> {
    if (entity !== 'playbook') throw new Error(`Unsupported sync entity: ${entity}`);
    return api.getPlaybook(entityId);
  },
  async sendMutations(operations: SyncMutation<Partial<Playbook>>[], idempotencyKey: string) {
    return api.syncMutations(operations, idempotencyKey);
  },
};

const engine = new SyncEngine<Partial<Playbook>>(remote, store);

export async function syncNow(): Promise<SyncReport> {
  if (!accountUserId) throw new Error('Sync account is not initialized');
  if (syncPromise) {
    // A save can happen while the initial pull is still running. Remember the
    // request so the shared promise performs one more pull/push pass instead
    // of reporting success while the newly queued mutation is still local.
    syncAgain = true;
    return syncPromise;
  }
  syncPromise = (async () => {
    let report: SyncReport = { workspaces: 0, applied: 0, conflicts: 0 };
    do {
      syncAgain = false;
      report = await engine.syncAll();
    } while (syncAgain);
    return report;
  })().finally(() => { syncPromise = null; });
  return syncPromise;
}

export type SyncNotification = { type?: string; workspace_id?: string };
const syncListeners = new Set<(event: SyncNotification) => void>();
export function subscribeSyncNotifications(listener: (event: SyncNotification) => void): () => void {
  syncListeners.add(listener);
  return () => { syncListeners.delete(listener); };
}
function emitSyncNotification(event: SyncNotification): void {
  for (const listener of syncListeners) listener(event);
}


export function startSyncEvents(onState?: (state: 'synced' | 'offline') => void, onEvent?: (event: SyncNotification) => void): void {
  if (eventSocket || !accountUserId) return;
  if (eventReconnectTimer !== null) {
    window.clearTimeout(eventReconnectTimer);
    eventReconnectTimer = null;
  }
  const configuredApiURL = import.meta.env.VITE_API_URL as string | undefined;
  const apiBaseURL = new URL(configuredApiURL || '/api/v1', window.location.origin);
  const socketURL = new URL(`${apiBaseURL.pathname.replace(/\/$/, '')}/events`, apiBaseURL.origin);
  socketURL.protocol = socketURL.protocol === 'https:' ? 'wss:' : 'ws:';
  const socket = new WebSocket(socketURL.toString());
  eventSocket = socket;
  const unsubscribe = onEvent ? subscribeSyncNotifications(onEvent) : () => {};
  socket.onmessage = (message) => {
    if (eventSocket !== socket) return;
    try {
      const event = JSON.parse(message.data as string) as SyncNotification;
      if (event.type === 'workspace_revoked' && event.workspace_id) void purgeWorkspace(event.workspace_id);
      emitSyncNotification(event);
      if (event.type === 'ready') void syncNow().then(() => { emitSyncNotification({type: 'reconnected'}); onState?.('synced'); }).catch(() => onState?.('offline'));
      if (event.type === 'sync' && event.workspace_id) void syncNow().then(() => onState?.('synced')).catch(() => onState?.('offline'));
      if (event.type === 'auth_revoked') onState?.('offline');
    } catch { /* malformed notifications are ignored; the next sync repairs state */ }
  };
  socket.onerror = () => onState?.('offline');
  socket.onclose = () => {
    unsubscribe();
    if (eventSocket !== socket) return;
    eventSocket = null;
    if (accountUserId) {
      eventReconnectTimer = window.setTimeout(() => {
        eventReconnectTimer = null;
        startSyncEvents(onState, onEvent);
      }, 4000);
    }
  };
}

export function stopSync(): void {
  if (eventReconnectTimer !== null) {
    window.clearTimeout(eventReconnectTimer);
    eventReconnectTimer = null;
  }
  eventSocket?.close();
  eventSocket = null;
}

export function getLocalPlaybooks(workspaceId = ''): Playbook[] {
  return [...localPlaybooks.values()].filter((item) => item.source !== 'team' && (!workspaceId || item.workspace_id === workspaceId)).sort((left, right) => new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime());
}

export function getPersonalWorkspaceId(): string {
  return personalWorkspaceId || 'personal';
}

export function pendingPlaybookMutations(): number {
  return queue.snapshot().filter((mutation) => mutation.status !== 'applied').length;
}

export function getPlaybookMutation(workspaceId: string, entityId: string): OfflineMutation<QueuedPlaybookMutation> | undefined {
  const effectiveWorkspaceId = workspaceId === 'personal' ? getPersonalWorkspaceId() : workspaceId;
  const workspaceIds = new Set([workspaceId, effectiveWorkspaceId]);
  return queue.snapshot().find((record) => workspaceIds.has(record.workspaceId)
    && record.entity === 'playbook'
    && record.entityId === entityId
    && record.status !== 'applied');
}

export async function enqueuePlaybookMutation(workspaceId: string, entityId: string | undefined, payload: QueuedPlaybookMutation, baseVersion = payload.version ?? 0): Promise<Playbook> {
  if (workspaceId !== 'personal' && workspaceId !== personalWorkspaceId) throw new Error('TEAM_ONLINE_REQUIRED');
  const id = entityId ?? idFor();
  const effectiveWorkspaceId = workspaceId === 'personal' ? getPersonalWorkspaceId() : workspaceId;
  // Coalesce only an edit that has never been sent. Once an operation is
  // in-flight, its operation id is already part of the server's idempotency
  // boundary and must not be reused for a new logical edit. Failed/conflict
  // records are superseded by the new local edit; any request that already
  // committed is still protected by base_version and will produce a conflict
  // rather than a duplicate write. The old entity-id scheme caused the second
  // edit of an existing playbook to be rejected as idempotency-key reuse.
  const pendingRecords = queue.snapshot().filter((record) => ['queued', 'failed', 'conflict'].includes(record.status)
    && record.workspaceId === effectiveWorkspaceId
    && record.entity === 'playbook'
    && record.entityId === id);
  const queued = pendingRecords.find((record) => record.status === 'queued');
  const queuedOperationId = queued?.operationId ?? queued?.id;
  const pending = queued && queuedOperationId !== id ? queued : undefined;
  for (const record of pendingRecords) {
    if (record === pending) continue;
    queue.remove(record.id);
    await safeDelete(`outbox:${record.id}`);
  }
  const pendingOperationId = pending?.operationId ?? pending?.id;
  let operationId = pendingOperationId || idFor();
  while (operationId === id) operationId = idFor();
  const recordId = pending ? pending.id : operationId;
  const mutation = await createSyncMutation({ operationId, workspaceId: effectiveWorkspaceId, entity: 'playbook', entityId: id, baseVersion, payload });
  const record = queue.enqueueLatest({ id: recordId, operationId: mutation.operation_id, workspaceId: effectiveWorkspaceId, entity: 'playbook', entityId: id, baseVersion, requestHash: mutation.request_hash, payload });
  await safePut(`outbox:${record.id}`, { kind: 'mutation', record });
  const local = normalizePlaybook({ ...payload, id, workspace_id: effectiveWorkspaceId, version: baseVersion }, id, effectiveWorkspaceId, baseVersion);
  await store.upsert('playbook', local);
  return local;
}

export function isOfflineError(error: unknown): boolean {
  return error instanceof TypeError || (error instanceof APIClientError && (error.status === 0 || error.status >= 500)) || isCursorExpired(error);
}
